/**
 * Per-page copilot context registry — the bridge between noki-fe pages
 * and the agent's `get_current_context` tool.
 *
 * Mirror of `actionRegistry.js` in shape: a module-level singleton with
 * subscribers + revision counter, plus a thin React hook that wraps the
 * imperative API in a `useEffect`. The choice of module-level state
 * (rather than a React Context Provider) keeps the registry reachable
 * from anywhere without requiring callers to wrap their tree, which
 * matches how `actionRegistry` works and lets `<NokiCopilot />` stay
 * mountable anywhere.
 *
 * Layered protocols (per voice_copilot_design.md §7 + §14.1):
 *
 *   The registry holds an N-deep stack of active protocols at any time:
 *   one for the page, one for an open drawer, one for an open modal,
 *   etc. Each entry declares its `level` ("page" | "drawer" | "modal" |
 *   "focus") and an optional `exclusive` flag.
 *
 *   `getMergedPageContext()` returns either:
 *     - the union of all active entries (default), with the deepest
 *       level winning on `pageId` / `description`, capabilities/toolIds
 *       unioned, salient shallow-merged; OR
 *     - if any active entry has `exclusive: true`, ONLY that entry's
 *       data — sibling protocols are suppressed. This is how the
 *       consent modal and the recording screen narrow the agent's
 *       surface to clinically-safe-only tools.
 *
 *   `active_protocols` (the metadata list) is always returned so the
 *   server-side filter (orchestrator) can reject tool calls whose tool
 *   isn't owned by an active protocol.
 *
 * What pages contribute:
 *   - description    — 1-3 sentence human description of this layer
 *   - capabilities   — verb-phrase list of what the user can do here
 *   - toolIds        — names of catalog tools active on this layer (the
 *                      orchestrator filters dispatched calls against
 *                      the union of these)
 *   - salient        — STRUCTURAL-ONLY runtime state (booleans, counts,
 *                      ids). NEVER patient names, DOBs, or clinical
 *                      values — those flow through audited tool calls.
 *                      Per INTEGRATION.md §3.
 *   - level          — "page" | "drawer" | "modal" | "focus"
 *                      (default "page"). Determines stacking order
 *                      and which layer wins on description/pageId.
 *   - exclusive      — boolean, default false. When true, suppresses
 *                      every other active layer's contributions —
 *                      agent sees only this layer's tools/capabilities.
 *                      Use sparingly: consent modals, focus-locked
 *                      regions like an active recording.
 *
 * Lifecycle:
 *   - Hook mounts  -> registerPageContext(entry)
 *   - Hook deps change -> register replaces the entry under the same id
 *     (revision bumps; panel re-publishes the merged snapshot)
 *   - Hook unmounts -> unregisterPageContext(id)
 */

import { useEffect, useMemo } from "react";

/** Level priority: higher = deeper / more specific. */
const LEVEL_PRIORITY = Object.freeze({
  page: 0,
  drawer: 1,
  modal: 2,
  focus: 3,
});

const VALID_LEVELS = new Set(Object.keys(LEVEL_PRIORITY));

/** Internal map: id -> ContextEntry. */
const entries = new Map();

/** Subscribers notified whenever the registry mutates. */
const subscribers = new Set();

let revision = 0;

const notify = () => {
  revision += 1;
  subscribers.forEach((cb) => {
    try {
      cb(revision);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[copilot] page-context subscriber threw:", error);
    }
  });
};

/**
 * @typedef {Object} DeclaredChild
 * @property {string} pageId          the child's stable pageId (e.g. "select_patient_drawer")
 * @property {string} shortDescription 1-line description used by the agent for narration
 * @property {string[]} capabilities  verb-phrase list of what the child enables
 * @property {"voice_action"|"click"|"automatic"} activationVia how the child becomes active
 * @property {string} activationVoiceTool name of the catalog tool that activates the child
 * @property {"drawer"|"modal"|"focus"|"page"} [level] level of the child when active; default "drawer"
 *
 * Latent declaration: parents publish their known children even when those
 * children's protocols aren't currently mounted. The agent sees them as
 * "advertised but not currently active — call activationVoiceTool to enter."
 *
 * @typedef {Object} ContextEntry
 * @property {string} id          unique per consumer; stable string
 * @property {string} pageId      semantic id ("patient_list", "consent_modal")
 * @property {string|null} [parentId] pageId of the parent protocol when this is
 *           a sub-view nested inside another. null = root (top-level page).
 * @property {string} description what this layer is, in 1-3 sentences
 * @property {string[]} capabilities verb-phrase list
 * @property {string[]} [toolIds]  catalog tool names active on this layer
 * @property {DeclaredChild[]} [declaredChildren] known sub-views advertised
 *           even when not currently active (closed popovers, collapsed
 *           panels, modals not yet opened, etc.). Each child publishes its
 *           pageId, capabilities, and the voice tool that activates it.
 * @property {Object<string, string|number|boolean|null>} [salient]
 *           structural-only runtime state. Never PHI.
 * @property {"page"|"drawer"|"modal"|"focus"} [level]
 *           stacking level, default "page"
 * @property {boolean} [exclusive] when true, suppresses other layers
 */

const SALIENT_SCALAR_TYPES = new Set(["string", "number", "boolean"]);

const sanitizeSalient = (raw) => {
  if (!raw || typeof raw !== "object") return undefined;
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v === null) {
      out[k] = null;
      continue;
    }
    if (SALIENT_SCALAR_TYPES.has(typeof v)) {
      out[k] = v;
      continue;
    }
    // Drop arrays / objects / functions silently — the wire schema is
    // dict[str, scalar] and the PHI policy says structural-only. A
    // misuse here shouldn't blow up the page.
    // eslint-disable-next-line no-console
    console.warn(
      "[copilot] page-context salient drop (non-scalar):",
      { key: k, type: typeof v },
    );
  }
  return out;
};

const normalizeLevel = (raw) => {
  if (typeof raw === "string" && VALID_LEVELS.has(raw)) return raw;
  return "page";
};

const VALID_ACTIVATION_VIA = new Set(["voice_action", "click", "automatic"]);

/** Validate + freeze a single declared-child entry. Drops malformed entries. */
const sanitizeDeclaredChild = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const pageId =
    typeof raw.pageId === "string" && raw.pageId.length > 0 ? raw.pageId : "";
  if (!pageId) return null;
  const shortDescription =
    typeof raw.shortDescription === "string" ? raw.shortDescription.trim() : "";
  const capabilities = Array.isArray(raw.capabilities)
    ? raw.capabilities.filter((c) => typeof c === "string" && c.trim())
    : [];
  const activationVia =
    typeof raw.activationVia === "string"
      && VALID_ACTIVATION_VIA.has(raw.activationVia)
      ? raw.activationVia
      : "voice_action";
  const activationVoiceTool =
    typeof raw.activationVoiceTool === "string"
    && raw.activationVoiceTool.length > 0
      ? raw.activationVoiceTool
      : "";
  const level = typeof raw.level === "string" && VALID_LEVELS.has(raw.level)
    ? raw.level
    : "drawer";
  return {
    pageId,
    shortDescription,
    capabilities,
    activationVia,
    activationVoiceTool,
    level,
  };
};

const sanitizeDeclaredChildren = (raw) => {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const item of raw) {
    const child = sanitizeDeclaredChild(item);
    if (child !== null) out.push(child);
  }
  return out;
};

/**
 * Imperative registration. Returns a cleanup function.
 * Pair with the `useCopilotPageContext` hook from React components.
 */
export function registerPageContext(entry) {
  if (!entry || typeof entry.id !== "string" || !entry.id) return () => {};
  if (typeof entry.pageId !== "string" || !entry.pageId) return () => {};

  const normalized = {
    id: entry.id,
    pageId: entry.pageId,
    parentId:
      typeof entry.parentId === "string" && entry.parentId.length > 0
        ? entry.parentId
        : null,
    description:
      typeof entry.description === "string" ? entry.description.trim() : "",
    capabilities: Array.isArray(entry.capabilities)
      ? entry.capabilities.filter((c) => typeof c === "string" && c.trim())
      : [],
    toolIds: Array.isArray(entry.toolIds)
      ? entry.toolIds.filter((t) => typeof t === "string" && t.trim())
      : [],
    declaredChildren: sanitizeDeclaredChildren(entry.declaredChildren),
    salient: sanitizeSalient(entry.salient),
    level: normalizeLevel(entry.level),
    exclusive: entry.exclusive === true,
    registeredAt: revision + 1,
  };

  entries.set(entry.id, normalized);
  notify();
  return () => {
    const current = entries.get(entry.id);
    // Only delete if WE are still the owner — a re-register with newer
    // content under the same id shouldn't be wiped by an earlier
    // mount's cleanup (matches the actionRegistry pattern).
    if (current && current.registeredAt === normalized.registeredAt) {
      entries.delete(entry.id);
      notify();
    }
  };
}

export function unregisterPageContext(id) {
  if (entries.delete(id)) notify();
}

const sortedEntries = () =>
  Array.from(entries.values()).sort((a, b) => {
    // Primary: level ascending (page first, focus last)
    const lp = LEVEL_PRIORITY[a.level] - LEVEL_PRIORITY[b.level];
    if (lp !== 0) return lp;
    // Secondary: registration time ascending (older first)
    return a.registeredAt - b.registeredAt;
  });

/**
 * Collect declared-child sub-views advertised by any active entry whose
 * own pageId is NOT currently active. These are "latent capabilities" —
 * the agent sees them as activatable via their declared voice tool, but
 * none are currently mounted. Drives the protocol-tree composition pattern
 * (closed popovers, collapsed panels, modals advertised from a parent).
 *
 * Dedupe by child pageId; first declaration wins (shallow-first traversal
 * of `ordered` already prioritises page-level declarations over deeper
 * overlays' redeclarations).
 */
const collectLatentCapabilities = (ordered) => {
  const activePageIds = new Set();
  for (const e of ordered) activePageIds.add(e.pageId);

  const seen = new Set();
  const out = [];
  for (const entry of ordered) {
    if (!Array.isArray(entry.declaredChildren) || entry.declaredChildren.length === 0) continue;
    for (const child of entry.declaredChildren) {
      if (activePageIds.has(child.pageId)) continue; // already active — not latent
      if (seen.has(child.pageId)) continue;
      seen.add(child.pageId);
      out.push({ ...child, parentPageId: entry.pageId });
    }
  }
  return out;
};

/**
 * Pick the entry whose pageId + description represent the active surface.
 *
 * `ordered` is shallow-first (level asc, then registration asc), so the last
 * element is the deepest. When an OVERLAY (drawer/modal/focus) is open it is
 * genuinely the active surface and wins — unchanged behavior.
 *
 * But at the PAGE tier we must not let an embedded CHILD page shadow its
 * parent route. An always-mounted dashboard widget (e.g. AddPatientCard
 * registering `pageId:"create_patient"`, `parentId:"dashboard"`, `level:"page"`)
 * would otherwise win purely by registration recency and report
 * `page_id=create_patient` while the route is `/home/dashboard` — and flip on
 * every re-registration. So among page-tier entries we prefer a ROOT page (one
 * that is NOT a child of another active entry); recency only breaks ties among
 * roots. This keeps `page_id` equal to the real route without changing any
 * entry's registration (the child stays in activeProtocols; its tools/
 * preconditions are unaffected).
 */
const resolveBasePageEntry = (ordered) => {
  const deepest = ordered[ordered.length - 1];
  // An overlay on top is the active surface — keep the deepest.
  if (deepest.level !== "page") return deepest;
  // Page tier only: prefer a root page over an embedded child page.
  const activePageIds = new Set(ordered.map((e) => e.pageId));
  const pageEntries = ordered.filter((e) => e.level === "page");
  const roots = pageEntries.filter(
    (e) => !e.parentId || !activePageIds.has(e.parentId),
  );
  const pool = roots.length > 0 ? roots : pageEntries;
  // pool preserves `ordered` (recency-ascending) order → last is most recent.
  return pool[pool.length - 1];
};

/**
 * Compute the merged snapshot from all active entries.
 *
 * Two modes per design doc §14.1:
 *   1. Default (no exclusive layer): union all entries. Capabilities and
 *      toolIds are deduped union in level order (page → drawer → modal
 *      → focus). Description is the deepest entry's. pageId is the
 *      deepest entry's. Salient is shallow-merged with deeper entries
 *      winning on key conflict.
 *   2. Exclusive (any entry has exclusive=true): only that entry's
 *      data is returned; sibling layers are suppressed. If multiple
 *      entries are exclusive, the deepest one wins (it shadows the
 *      shallower exclusive too).
 *
 * Always returns an `activeProtocols` list in stack order (shallowest
 * first) so the panel / server can filter dispatched tool calls against
 * the active protocol set.
 *
 * Also returns `latentCapabilities` — sub-views declared by active
 * entries via `declaredChildren` whose own protocols aren't yet
 * mounted. The agent sees these as activatable affordances ("you can
 * also start an encounter by saying 'start a new encounter'") even
 * when their UI isn't on screen yet.
 */
export function getMergedPageContext() {
  const ordered = sortedEntries();
  if (ordered.length === 0) {
    return {
      pageId: null,
      description: "",
      capabilities: [],
      toolIds: [],
      salient: {},
      activeProtocols: [],
      latentCapabilities: [],
    };
  }

  // Find the deepest exclusive entry, if any.
  let exclusiveEntry = null;
  for (const e of ordered) {
    if (e.exclusive) {
      // ordered is shallow-first; later iterations are deeper.
      exclusiveEntry = e;
    }
  }

  const activeProtocols = ordered.map((e) => ({
    id: e.id,
    pageId: e.pageId,
    parentPageId: e.parentId,
    level: e.level,
    exclusive: e.exclusive,
  }));

  const latentCapabilities = collectLatentCapabilities(ordered);

  if (exclusiveEntry) {
    // Exclusive layer suppresses siblings AND latent declarations from
    // parent layers — the agent's surface narrows to ONLY the exclusive
    // layer's contribution + its own latent declarations (if any).
    const exclusiveLatent = collectLatentCapabilities([exclusiveEntry]);
    return {
      pageId: exclusiveEntry.pageId,
      description: exclusiveEntry.description,
      capabilities: [...exclusiveEntry.capabilities],
      toolIds: [...(exclusiveEntry.toolIds || [])],
      salient: { ...(exclusiveEntry.salient || {}) },
      activeProtocols,
      latentCapabilities: exclusiveLatent,
    };
  }

  // Union mode.
  const seenCap = new Set();
  const capabilities = [];
  const seenTool = new Set();
  const toolIds = [];
  for (const e of ordered) {
    for (const c of e.capabilities) {
      if (!seenCap.has(c)) {
        seenCap.add(c);
        capabilities.push(c);
      }
    }
    for (const t of e.toolIds || []) {
      if (!seenTool.has(t)) {
        seenTool.add(t);
        toolIds.push(t);
      }
    }
  }
  const salient = {};
  for (const e of ordered) {
    if (e.salient) Object.assign(salient, e.salient);
  }
  // The winning entry supplies pageId + description.
  const winner = resolveBasePageEntry(ordered);
  return {
    pageId: winner.pageId,
    description: winner.description,
    capabilities,
    toolIds,
    salient,
    activeProtocols,
    latentCapabilities,
  };
}

/** Subscribe to revision changes. Returns an unsubscribe fn. */
export function subscribeCopilotPageContext(callback) {
  if (typeof callback !== "function") return () => {};
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

export function getCopilotPageContextRevision() {
  return revision;
}

/**
 * React hook for noki-fe pages, drawers, and modals.
 *
 *   useCopilotPageContext({
 *     id: "patients_v2/FhirPatientList",
 *     pageId: "patient_list",
 *     description: "Roster of all patients in this clinic ...",
 *     capabilities: ["Filter by name", "Sort by last visit", ...],
 *     toolIds: ["filter_patient_list_by_name", "select_patient"],
 *     salient: { filter_active: !!filterText, visible_row_count: rows.length },
 *     // For drawers / modals:
 *     // level: "drawer",
 *     // exclusive: true,   // narrows agent to this layer only
 *   });
 *
 * Memoize `capabilities`, `toolIds`, and `salient` at the call site
 * (`useMemo` on the consumer side) so the effect doesn't re-fire on every
 * render. The hook itself does no shallow comparison — it trusts the
 * caller's memoization, same as `useCopilotAction`.
 *
 * The cleanup runs on unmount (`useEffect` return) and on dep change
 * before the next register, so a page that re-renders with new salient
 * state replaces its entry in place rather than accumulating duplicates.
 */
export function useCopilotPageContext({
  id,
  pageId,
  parentId,
  description,
  capabilities,
  toolIds,
  declaredChildren,
  salient,
  level,
  exclusive,
}) {
  // Only stable primitives + memoized objects from the caller go into
  // the deps. The capabilities/toolIds/salient/declaredChildren
  // identities are the caller's responsibility to keep stable across
  // renders (use useMemo).
  useEffect(() => {
    if (!id || !pageId) return undefined;
    return registerPageContext({
      id,
      pageId,
      parentId,
      description,
      capabilities,
      toolIds,
      declaredChildren,
      salient,
      level,
      exclusive,
    });
  }, [
    id,
    pageId,
    parentId,
    description,
    capabilities,
    toolIds,
    declaredChildren,
    salient,
    level,
    exclusive,
  ]);
}

/**
 * Convenience: build a stable empty-salient memo so callers can write
 *   const salient = useEmptySalient();
 * for pages that don't have any structural runtime state to share.
 */
export function useEmptySalient() {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({}), []);
}
